/**
 * "ADMIN — WHAT <LOCATION> IS ALLOWED". Super Admin's grants and caps, on
 * top of the settings card. Only Super Admin sees this; the merchant sees the
 * result as locked rows.
 *
 *   Emergency override — the REGISTER's `overrideActive`
 *   Allow tips on card / on cash — the location's `tipsEnabled` / `cashTipsEnabled`
 *   Surcharge approved — the location's `surchargeEnabled`, backed by an active
 *   agreement whose rate and flat fee are the ceilings
 */
import React from 'react';
import { ShieldAlert, DollarSign, Banknote, Percent } from 'lucide-react';
import { RegisterState, GrantState, activeAgreementOf } from './model';
import { Card, Row, Switch, ScopeBadge, Bad, Warn } from './ui';

export const AdminGrantsCard: React.FC<{
  locationName?: string;
  draft: RegisterState;
  onChange: (next: RegisterState) => void;
  grants: GrantState;
  onGrantsChange: (next: GrantState) => void;
  gateway: any | null;
  onChangeAgreement: () => void;
  disabled?: boolean;
}> = ({ locationName, draft, onChange, grants, onGrantsChange, gateway, onChangeAgreement, disabled }) => {
  const gwRead = gateway !== null && gateway !== undefined;
  const agreement = activeAgreementOf(gateway);
  const maxRate = Number(gateway?.maxSurchargeRate ?? agreement?.maxRate ?? 0);
  const maxFlat = Number(gateway?.maxSurchargeFlat ?? agreement?.maxFlat ?? 0);
  const ov = draft.overrideActive;
  const approved = grants.surchargeEnabled && !!agreement;

  return (
    <Card
      dark
      title={`Admin — what ${locationName || 'this location'} is allowed`}
      sub="Only Super Admin sees or edits this. The merchant sees the result as locked rows."
      badge={<ScopeBadge kind="adm">Admin</ScopeBadge>}
    >
      <Row
        testId="pt-admin-override"
        icon={<ShieldAlert size={18} />}
        iconOn={ov}
        title="Emergency override"
        desc={ov
          ? <Bad>ON — every assigned tender is forced on and the merchant can't change them.</Bad>
          : 'Forces every assigned tender on, regardless of the merchant’s choices. This till only.'}
        right={<Switch on={ov} label="Emergency override" disabled={disabled} onChange={(v) => onChange({ ...draft, overrideActive: v })} />}
      />
      <Row
        testId="pt-admin-cardtips"
        icon={<DollarSign size={18} />}
        iconOn={grants.tipsEnabled}
        title="Allow tips on card"
        desc={gwRead ? 'Every till at the location. The merchant still switches tipping on and sets the percentages.' : <Warn>Location settings could not be read.</Warn>}
        right={<Switch on={grants.tipsEnabled} label="Allow card tips" disabled={disabled || !gwRead} onChange={(v) => onGrantsChange({ ...grants, tipsEnabled: v })} />}
      />
      <Row
        testId="pt-admin-cashtips"
        icon={<Banknote size={18} />}
        iconOn={grants.cashTipsEnabled}
        title="Allow tips on cash"
        desc="Independent of card tips — a cash-only location can turn tips on."
        right={<Switch on={grants.cashTipsEnabled} label="Allow cash tips" disabled={disabled || !gwRead} onChange={(v) => onGrantsChange({ ...grants, cashTipsEnabled: v })} />}
      />
      <Row
        testId="pt-admin-surcharge"
        icon={<Percent size={18} />}
        iconOn={approved}
        title="Surcharge approved"
        desc={!gwRead
          ? <Warn>Location settings could not be read.</Warn>
          : !agreement
            ? <Warn>No signed agreement is active — add one before surcharge can be approved. Until then surcharge is locked off on every till.</Warn>
            : approved
              ? <>Active agreement: <b>{agreement.agreementName || agreement.fileName || 'signed agreement'}</b> · max {maxRate.toFixed(2)}% + ${maxFlat.toFixed(2)}</>
              : <>Not approved — surcharge is locked off on every till. Agreement on file: <b>{agreement.agreementName || agreement.fileName}</b>.</>}
        right={<>
          {gwRead && (
            <button
              type="button"
              onClick={onChangeAgreement}
              className="min-h-[44px] rounded-[10px] border border-stone-200 bg-white px-3 text-[13px] font-medium text-stone-800 hover:border-ruby hover:text-ruby-deep"
            >
              {agreement ? 'Change agreement' : 'Add agreement'}
            </button>
          )}
          <Switch
            on={approved}
            label="Approve surcharge"
            disabled={disabled || !gwRead || (!agreement && !grants.surchargeEnabled)}
            locked={gwRead && !agreement}
            /* With no agreement the only move is clearing a stale flag. */
            onChange={() => onGrantsChange({ ...grants, surchargeEnabled: agreement ? !grants.surchargeEnabled : false })}
          />
        </>}
      />
    </Card>
  );
};

export default AdminGrantsCard;
